/**
 * Theme preference (light / dark / system) for the Pellerex dashboard.
 *
 * The resolved theme is a single `dark` class on <html>; every colour token in
 * theme.css reads from that. index.html carries a tiny inline copy of the
 * resolve logic so the class is set before first paint (no light flash).
 *
 * Vocabulary (preference values, storage key, event, class) lives in
 * common/themeConstants.js; it is re-exported here so existing importers of
 * `common/theme` are unaffected.
 */
import {
  ThemePreference,
  THEME_STORAGE_KEY,
  THEME_CHANGE_EVENT,
  DARK_CLASS,
} from "./themeConstants";

export { ThemePreference, THEME_STORAGE_KEY, THEME_CHANGE_EVENT };

const VALID_PREFERENCES = Object.values(ThemePreference);

// The mobile browser-chrome colour (<meta name="theme-color">) mirrors the page
// background. theme.css is the single source: read the active --background token
// after the theme class is applied rather than hard-coding hexes here.
const readBackgroundToken = () => {
  try {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue("--background")
      .trim();
    return value || null;
  } catch {
    return null;
  }
};

const applyThemeColorMeta = () => {
  const meta = document.querySelector('meta[name="theme-color"]');
  const color = readBackgroundToken();
  if (meta && color) {
    meta.setAttribute("content", color);
  }
};

const systemDarkQuery = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;

export const getThemePreference = () => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return VALID_PREFERENCES.includes(stored) ? stored : ThemePreference.System;
  } catch {
    return ThemePreference.System;
  }
};

export const resolveTheme = (preference = getThemePreference()) => {
  if (preference === ThemePreference.System) {
    return systemDarkQuery()?.matches ? ThemePreference.Dark : ThemePreference.Light;
  }
  return preference;
};

const notify = (preference, resolved) => {
  window.dispatchEvent(
    new CustomEvent(THEME_CHANGE_EVENT, { detail: { preference, resolved } })
  );
};

/** Applies the resolved theme to the document. Returns the resolved theme. */
export const applyTheme = (preference = getThemePreference()) => {
  const resolved = resolveTheme(preference);
  const root = document.documentElement;
  root.classList.toggle(DARK_CLASS, resolved === ThemePreference.Dark);
  root.style.colorScheme = resolved;
  applyThemeColorMeta();
  return resolved;
};

/** Removes the theme from the document (used by surfaces that are light-only). */
export const clearTheme = () => {
  const root = document.documentElement;
  root.classList.remove(DARK_CLASS);
  root.style.colorScheme = "light";
  applyThemeColorMeta();
};

export const setThemePreference = (preference) => {
  const next = VALID_PREFERENCES.includes(preference) ? preference : ThemePreference.System;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    // Storage unavailable (private mode, blocked) — theme still applies for this page load.
  }
  const resolved = applyTheme(next);
  notify(next, resolved);
  return resolved;
};

/**
 * Applies the stored preference and keeps it in sync with the OS while the
 * preference is "system". Returns a cleanup function.
 */
export const initTheme = () => {
  applyTheme();
  const query = systemDarkQuery();
  if (!query) {
    return () => {};
  }
  const onSystemChange = () => {
    const preference = getThemePreference();
    if (preference === ThemePreference.System) {
      notify(preference, applyTheme(preference));
    }
  };
  query.addEventListener("change", onSystemChange);
  return () => query.removeEventListener("change", onSystemChange);
};

/** Subscribes to theme changes (user or OS). Returns an unsubscribe function. */
export const subscribeToTheme = (listener) => {
  const handler = (event) => listener(event.detail);
  window.addEventListener(THEME_CHANGE_EVENT, handler);
  return () => window.removeEventListener(THEME_CHANGE_EVENT, handler);
};
